// @ts-check
import React, { useEffect, useState } from 'react';
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import LnIcon from "components/LnIcon";
import { Button, Modal, ModalBody, ModalFooter } from 'reactstrap';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { DayPicker, addToRange } from 'react-day-picker';
import "react-day-picker/style.css";
import 'assets/scss/react-datepicker-custom.scss';
import { useI18n } from "components/lni18n";
import useTypedSelector from "utils/useTypedSelector";
import 'dayjs/locale/sv';
import 'dayjs/locale/da';
import 'dayjs/locale/en-gb';
import { sv, da, enGB } from "react-day-picker/locale";
//  import type { Nav } from 'react-day-picker';

/**
 * DateTimePicker
 * @module components/DateTimePicker/DateTimePicker
 * @return a Date selected by the user.
 * @param {{
    StartDate:  Date,
    EndDate: Date,
    DatePickerId?: string,
    onChange: Function,
    showIcon?: boolean,
    showMonthDropdown?: boolean,
    showYearDropdown?: boolean,
    showTimeSelect?: boolean,
    showWeekNumbers?: boolean,
    timeCaption?: string, // time
    timeFormat?: string, // HH:mm
    timeIntervals?: number, // {15}
    renderDays: 'all' | 'past' | 'future',
    lockStartDate?:boolean,
    children?: any

}} props
 */
const DateSpanPicker = (props) => {
    const {
        StartDate,
        EndDate,
        onChange,
        showIcon,
        showMonthDropdown,
        showYearDropdown,
        showTimeSelect,
        showWeekNumbers,
        timeCaption,
        timeFormat,
        timeIntervals,
        renderDays,
        lockStartDate,
        children } = props

    /** @type {{ from: Date, to: Date }} */
    const initialState = {
        from: StartDate,
        to: EndDate,
    };
    const [state, setState] = useState(initialState);
    const { languageService: t } = useI18n();
    const [show, setShowDialog] = useState(undefined)
    const [month, setMonth] = useState(StartDate);
    const [dropDownDate, setDropDownDate] = useState(StartDate);

    const currentLang = useTypedSelector(state => state.text.currentLang);

    const [startTime, setStartTime] = useState(dayjs(StartDate));
    const [endTime, setEndTime] = useState(dayjs(EndDate));

    let locale = enGB;

    switch (currentLang.Short) {
        case "sv":
            locale = sv
            break;

        case "da":
            locale = da
            break;
    }

    dayjs.extend(localeData);
    const globalLocaleData = dayjs.localeData()
    const localMonths = globalLocaleData.months();
    // const localDaysShort = globalLocaleData.weekdaysShort();
    // const localDays = globalLocaleData.weekdays();
    const firstDayOfWeek = globalLocaleData.firstDayOfWeek();


    useEffect(() => {
        if (showTimeSelect) {
            setStartTime(startTime.hour(0).minute(0));
            setEndTime(endTime.hour(23).minute(59));
        }
        // eslint-disable-next-line 
    }, []);


    const setstartMonth = (index) => {
        const d = dayjs(dropDownDate).month(index).toDate();
        setDropDownDate(d)
        setMonth(d);
    }

    const setstartYear = (y) => {
        const d = dayjs(dropDownDate).year(y).toDate();
        setDropDownDate(d)
        setMonth(d);
    }

    const setToday = () => {
        setDropDownDate(new Date());
        setMonth(new Date())
    }

    const handleResetClick = () => {
        const start = lockStartDate ? StartDate : null;
        setState({ from: start, to: null });
    }

    const handleDayClick = (day) => {
        const range = addToRange(day, state);

        if (lockStartDate) {
            range.from = StartDate;
        }

        /* Set time at from and to dates */
        setState({
            from: adjustTime(range.from, startTime),
            to: adjustTime(range.to, endTime)
        });
    }

    const adjustTime = (date, mom) => {
        if (!date) {
            return null;
        }

        return dayjs(date).hour(mom.hour()).minute(mom.minute()).toDate();
    }


    let today = dayjs().hour(0).minute(1);
    let disabledDays = (day) => false;

    if (renderDays === 'past') {
        disabledDays = (day) => {
            return today.isBefore(day, 'day');
        }
    }
    if (renderDays === 'future') {
        disabledDays = (day) => {
            return today.isAfter(day, 'day');
        }
    }

    if (lockStartDate) {
        disabledDays = (day) => {
            return dayjs(StartDate).isAfter(day, 'day');
        }
    }

    const years = [];
    const startYear = (new Date()).getFullYear() - 10;
    for (let i = startYear; i <= (startYear + 20); i += 1) {
        years.push(i);
    }

    const handleMonthChange = (month) => {
        setDropDownDate(month);
        setMonth(month);
    }

    const closeAndSetDate = () => {
        setShowDialog(false);
        onChange(state);
    }

    /**
     * 
     * @param {Object} prps 
     * @return {React.ReactElement}
     */
    const Navbar = (prps) => {
        return (
            <div className="navbar mt-3 d-flex justify-content-between">
                <button className="btn btn-link" onClick={() => prps.onPreviousClick()}>
                    <LnIcon name="previous" className="mr-2" />
                    {t.getText("previous")}
                </button>
                <button className="btn btn-link" onClick={() => prps.onNextClick()}>
                    {t.getText("next")}
                    <LnIcon className="ml-2" name="next" />
                </button>
            </div>
        );
    }

    const { from, to } = state;
    const modifiers = { start: from, end: to };

    /**
     * 
     * @param {React.ChangeEvent<HTMLInputElement>} e 
     * @param {string} which 
     */
    const handleTime = (e, which) => {
        const [hour, minute] = e.target.value.split(":");
        const d = dayjs(which === "start" ? state.from : state.to)
            .hour(parseInt(hour, 10))
            .minute(parseInt(minute, 10));

        if (which === "start") {
            setStartTime(d);
            setState({ ...state, from: d.toDate() });
        } else if (which === "end") {
            setEndTime(d);
            setState({ ...state, to: d.toDate() });
        }
    }

    const renderTimePicker = (which) => {
        if (which !== "start" && which !== "end") {
            throw new Error("which must be 'start' or 'end'");
        }
        const stateValue = which === "start" ? state.from : state.to;
        const time = dayjs(stateValue); /* Dayjs object*/
        const label = which === "start" ? t.getText("start.time") : t.getText("end.time"); /* Start Time, End Time */
        const isSameDate = dayjs(state.from).isSame(dayjs(state.to), "day");
        const displayFormat = timeFormat ?? "HH:mm";

        return !showTimeSelect
            ? <span>{t.getShortDateString(stateValue)}</span>
            : lockStartDate && which === "start"
                ? <span>{time.format(displayFormat)}</span>
                : <>
                    {isSameDate
                        ? <span>{label}</span>
                        : <span style={{ minWidth: "83px" }}>
                            {t.getShortDateString(stateValue)}
                        </span>
                    }
                    <input
                        type="time"
                        name={`time_${which}`}
                        value={time.format(displayFormat)}
                        onChange={e => handleTime(e, which)}
                        step="300" // 5 min
                        className="form-control"
                        required
                    />
                </>;
    }

    const showPicker = () => {
        setState({
            from: StartDate,
            to: EndDate
        });

        setShowDialog(true);
    }

    const isValid = dayjs(state.to).isAfter(dayjs(state.from));

    return (<>
        <button className="btn btn-link px-3" type="button" onClick={() => showPicker()} >
            {!dayjs(StartDate).isSame(dayjs(EndDate), "day") ? (

                <div className="d-flex justify-content-center align-items-center pl-2">

                    <div className="d-flex justify-content-start flex-column mr-2">
                        <span >{t.getText("startdate")}</span>
                        {showTimeSelect ?
                            <span className="white-space-nowrap">{t.getShortDateTimeString(StartDate)}</span> :
                            <span className="white-space-nowrap">{t.getShortDateString(StartDate)}</span>
                        }

                    </div>
                    {showIcon && <LnIcon className="ml-3 icon-medium" name="datepicker-icon" />}
                    <div className="d-flex flex-column ml-3">
                        <span>{t.getText("enddate")}</span>
                        {showTimeSelect ?
                            <span className="white-space-nowrap">{t.getShortDateTimeString(EndDate)}</span> :
                            <span className="white-space-nowrap">{t.getShortDateString(EndDate)}</span>
                        }
                    </div>
                    <div>{children}</div>
                </div>)
                : (
                    <div className="d-flex justify-content-center align-items-start pl-2 mt-3">
                        <div className="mr-2">
                            <div >{t.getText("date")}</div>
                            <div className="white-space-nowrap">{t.getShortDateString(StartDate)}</div>
                        </div>

                        {showTimeSelect && <>
                            <div className="mr-2">
                                <div >{t.getText("start.time")}</div>
                                <div className="white-space-nowrap">{t.getTimeString(StartDate)}</div>
                            </div>

                            <div className="mr-2">
                                <div >{t.getText("end.time")}</div>
                                <div className="white-space-nowrap">{t.getTimeString(EndDate)}</div>
                            </div>
                        </>}
                        {showIcon && <LnIcon className="ml-3 mt-1 icon-medium" name="datepicker-icon" />}
                        <div>{children}</div>
                    </div>
                )}
        </button>

        <Modal backdrop="static" role="dialog" isOpen={show} toggle={() => setShowDialog(false)} size="lg" >
            <ModalBody tabIndex={-1}>
                {(showMonthDropdown || showYearDropdown) &&
                    <div className="d-flex justify-content-center">
                        {showMonthDropdown &&
                            <UncontrolledDropdown >
                                <DropdownToggle caret className="btn btn-primary btn-small">
                                    {dayjs(dropDownDate).format("MMM")}
                                </DropdownToggle>
                                <DropdownMenu>
                                    {localMonths.map((m, i) => <DropdownItem className={i === dropDownDate.getMonth() ? "font-weight-bold text-success" : ""} key={i} onClick={() => setstartMonth(i)} >{m}</DropdownItem>)}
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        }

                        {showYearDropdown &&
                            <UncontrolledDropdown className="ml-5">
                                <DropdownToggle caret className="btn btn-primary btn-small">
                                    {dayjs(dropDownDate).format("YYYY")}
                                </DropdownToggle>
                                <DropdownMenu >
                                    {years.map((y) => <DropdownItem className={y === today.year() ? "font-weight-bold text-success" : ""} key={y} onClick={() => setstartYear(y)} >{y}</DropdownItem>)}
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        }
                    </div>
                }

                <div className="d-flex m-auto justify-content-center">

                    <DayPicker
                        mode="range"
                        className="range"
                        classNames={{
                            today: "datepicker-today",
                        }}
                        onDayClick={(d) => handleDayClick(d)}
                        defaultMonth={state.from}
                        numberOfMonths={window.outerWidth < 900 ? 1 : 2}
                        showOutsideDays={true}
                        showWeekNumber={showWeekNumbers}
                        disabled={disabledDays}
                        month={month}
                        locale={locale}
                        modifiers={modifiers}
                        onMonthChange={handleMonthChange}
                        selected={{ from: state.from, to: state.to }}
                        components={{
                            Nav: Navbar
                        }}
                        /* @ts-ignore */
                        weekStartsOn={firstDayOfWeek}
                    />
                </div>

                <div>
                    {!state.from && !state.to && t.getText('set.startdate')}
                    {state.from && !state.to && t.getText('set.enddate')}
                    {state.from &&
                        state.to &&
                        <div className="d-flex justify-content-around">

                            <div className="d-flex flex-wrap">

                                {!dayjs(state.from).isSame(dayjs(state.to), "day") ? (<>
                                    <div className="d-flex flex-column mr-5 mb-3">
                                        <span >{t.getText("startdate")}</span>
                                        <div className="d-flex align-items-center">
                                            {renderTimePicker("start")}
                                        </div>

                                    </div>
                                    <div className="d-flex flex-column mr-5">
                                        <span >{t.getText("enddate")}</span>
                                        <div className="d-flex align-items-center">
                                            {renderTimePicker("end")}
                                        </div>
                                    </div>
                                </>)
                                    :
                                    (
                                        <div className="d-flex justify-content-start align-items-start pl-2 flex-wrap">
                                            <div className="d-flex flex-column mr-2 ">
                                                <span >{t.getText("date")}</span>
                                                <span >{t.getShortDateString(state.from)}</span>
                                            </div>

                                            {showTimeSelect && <div className="d-flex">
                                                <div className="d-flex flex-column mr-2">
                                                    {renderTimePicker("start")}
                                                </div>

                                                <div className="d-flex flex-column mr-2">
                                                    {renderTimePicker("end")}
                                                </div>
                                            </div>}
                                        </div>
                                    )}
                            </div>

                            <div className="d-flex flex-wrap ml-4">
                                <button className={'btn btn-small btn-primary ml-3 mb-4'} onClick={() => setToday()}>{t.getText("today")}</button>
                                <button className="ml-3 btn btn-small btn-inverse" onClick={handleResetClick}>
                                    {t.getText("reset")}
                                </button>
                            </div>
                        </div>
                    }
                </div>
                {!isValid && <div className="small alert alert-warning preserve-white">{t.getText("correct_eventdates_alert")}</div>}

            </ModalBody>
            <ModalFooter className="d-flex justify-content-between">
                <Button className={"btn btn-inverse ml-2"} onClick={() => setShowDialog(false)}>{t.getText("cancel")}</Button>
                <Button className={"btn btn-primary"} onClick={closeAndSetDate} disabled={!isValid}>{t.getText("select")}</Button>
            </ModalFooter>
        </Modal>
    </>
    );
}

export default DateSpanPicker;